import React from "react";

import DescriptionIcon from "@mui/icons-material/Description";
import SyncIcon from "@mui/icons-material/Sync";
import { keyframes } from "@mui/material";

import {
  BuildIcon,
  CloneIcon,
  PushIcon,
  DeployIcon,
  PipelineIcon,
  SuccessIcon,
  ErrorIcon as RushaErrorIcon,
  PendingIcon,
  RunningIcon,
} from "@shell/components/icons";

export const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

export const BUILD_PHASE_STEPS: Record<
  string,
  { label: string; icon: React.ReactElement }
> = {
  checkout: { label: "Checkout source", icon: <CloneIcon size={20} /> },
  install: { label: "Install dependencies", icon: <BuildIcon size={20} /> },
  build: { label: "Build image", icon: <BuildIcon size={20} /> },
  push: { label: "Push to registry", icon: <PushIcon size={20} /> },
};

export const DEPLOY_PHASE_STEPS: Record<
  string,
  { label: string; icon: React.ReactElement }
> = {
  render: { label: "Render manifests", icon: <DescriptionIcon /> },
  commit: { label: "Commit to GitOps", icon: <CloneIcon size={20} /> },
  sync: { label: "Sync ArgoCD", icon: <SyncIcon /> },
  verify: { label: "Verify health", icon: <SuccessIcon size={20} /> },
};

export const CI_JOB_STEPS: Record<
  string,
  { label: string; icon: React.ReactElement }
> = {
  lint: { label: "Lint", icon: <CloneIcon size={20} /> },
  test: { label: "Run tests", icon: <PipelineIcon size={20} /> },
  "type-check": { label: "Type check", icon: <CloneIcon size={20} /> },
  build: { label: "Build", icon: <BuildIcon size={20} /> },
};

export const EVENT_STEP_MAP: Record<
  string,
  { label: string; icon: React.ReactElement; useNameFromMeta?: boolean }
> = {
  deployment_build_started: {
    label: "Build triggered",
    icon: <DeployIcon size={20} />,
  },
  deployment_build_completed: {
    label: "Build completed",
    icon: <SuccessIcon size={20} />,
  },
  deployment_build_failed: {
    label: "Build failed",
    icon: <RushaErrorIcon size={20} />,
  },
  deployment_build_service_started: {
    label: "Build",
    icon: <BuildIcon size={20} />,
    useNameFromMeta: true,
  },
  deployment_build_service_completed: {
    label: "Build",
    icon: <SuccessIcon size={20} />,
    useNameFromMeta: true,
  },
  deployment_build_service_failed: {
    label: "Build",
    icon: <RushaErrorIcon size={20} />,
    useNameFromMeta: true,
  },
  deployment_deploy_started: {
    label: "Deploy triggered",
    icon: <DeployIcon size={20} />,
  },
  deployment_deploy_completed: {
    label: "Deploy completed",
    icon: <SuccessIcon size={20} />,
  },
  deployment_deploy_failed: {
    label: "Deploy failed",
    icon: <RushaErrorIcon size={20} />,
  },
  deployment_deploy_service_started: {
    label: "Deploy",
    icon: <DeployIcon size={20} />,
    useNameFromMeta: true,
  },
  deployment_deploy_service_completed: {
    label: "Deploy",
    icon: <SuccessIcon size={20} />,
    useNameFromMeta: true,
  },
  deployment_deploy_service_failed: {
    label: "Deploy",
    icon: <RushaErrorIcon size={20} />,
    useNameFromMeta: true,
  },
  deployment_cdn_deploy_started: {
    label: "CDN Deploy",
    icon: <PushIcon size={20} />,
    useNameFromMeta: true,
  },
  deployment_cdn_deploy_completed: {
    label: "CDN Deploy",
    icon: <SuccessIcon size={20} />,
    useNameFromMeta: true,
  },
  deployment_cdn_deploy_failed: {
    label: "CDN Deploy",
    icon: <RushaErrorIcon size={20} />,
    useNameFromMeta: true,
  },
  deployment_vcs_provisioning_started: {
    label: "VCS Provisioning",
    icon: <DeployIcon size={20} />,
  },
  deployment_vcs_provisioning_completed: {
    label: "VCS Provisioned",
    icon: <SuccessIcon size={20} />,
  },
  deployment_vcs_provisioning_failed: {
    label: "VCS Provisioning Failed",
    icon: <RushaErrorIcon size={20} />,
  },
  deployment_vcs_repo_created: {
    label: "Repo Created",
    icon: <SuccessIcon size={20} />,
  },
  deployment_vcs_content_copied: {
    label: "Content Copied",
    icon: <SuccessIcon size={20} />,
  },
  deployment_vcs_branches_created: {
    label: "Branches Created",
    icon: <SuccessIcon size={20} />,
  },
  deployment_vcs_webhook_installed: {
    label: "Webhook Installed",
    icon: <SuccessIcon size={20} />,
  },
  deployment_github_workflow_dispatched: {
    label: "CI Workflow Triggered",
    icon: <DeployIcon size={20} />,
  },
  deployment_github_workflow_in_progress: {
    label: "CI Workflow Running",
    icon: <RunningIcon size={20} />,
  },
  deployment_github_workflow_completed: {
    label: "CI Workflow Completed",
    icon: <SuccessIcon size={20} />,
  },
  deployment_github_workflow_failed: {
    label: "CI Workflow Failed",
    icon: <RushaErrorIcon size={20} />,
  },
  deployment_build_timed_out: {
    label: "Build Timed Out",
    icon: <RushaErrorIcon size={20} />,
  },
  deployment_build_queued: {
    label: "Build Queued",
    icon: <PendingIcon size={20} />,
  },
  deployment_deploy_queued: {
    label: "Deploy Queued",
    icon: <PendingIcon size={20} />,
  },
};

export const STEP_ICON: Record<string, React.ReactElement> = {
  completed: <SuccessIcon size={20} />,
  in_progress: <RunningIcon size={20} />,
  failed: <RushaErrorIcon size={20} />,
  pending: <PendingIcon size={20} />,
  queued: <PendingIcon size={20} />,
  skipped: <PendingIcon size={20} />,
};
